import { coreSync } from './core';
import { FileRefused, uploadErrorMessage } from './fileLimits';

// The no-progress watchdog every upload on the web runs under (#612).
//
// The rule and its numbers are the core's (core/src/files.rs, the
// `upload_watch_*` section): an upload whose transferred byte count has not
// moved for 45 s — plus the time the chunk under way takes at 150 KiB/s,
// below — is cancelled and fails with the timeout sentence, and the row
// offers Try again. Both phones run the same rule over their own SDKs. What is
// here is the browser's half: a timer that looks once a tick, and the Storage
// task to cancel.
//
// Needed on top of the 15 s retry setting (src/lib/firebase.js): that bounds
// a request that fails, and a connection that goes silent with its socket
// open never fails.
//
// The web's step is the next resumable chunk, as Android's is, not 0. The SDK
// reports bytes from XHR upload progress events, and a browser need not fire
// them finely: Chrome may fire a handful per request, or none, so the count
// moves only when a chunk lands. A body buffered whole by a proxy or a
// scanner looks the same. Chunks double from 256 KiB, so without the
// allowance a live upload of a large file would be cancelled during a big
// chunk. So the watchdog allows for the chunk under way (`uploadStep`): a
// stall is noticed later on a big file, and a live upload is never cut.

/** The watchdog's numbers: `{ stallSeconds, slowestBytesPerSecond, tickMs, asleepMs }`. */
export const uploadWatchPolicy = () => coreSync().uploadWatchPolicy();
/** A watch on an upload whose bytes start at `now` (milliseconds). */
export const uploadWatchStart = (now) => coreSync().uploadWatchStart(Number(now));
/** A progress report: the clock restarts when the count changed. */
export const uploadWatchProgress = (watch, transferred, now) =>
  coreSync().uploadWatchProgress(watch, Number(transferred), Number(now));
/** A look at `now`; `stalled` on the answer means cancel. */
export const uploadWatchLook = (watch, now, stepBytes = 0) =>
  coreSync().uploadWatchLook(watch, Number(now), Number(stepBytes) || 0);
/** How long the count may stand still, in milliseconds. */
export const uploadStallAllowanceMs = (stepBytes = 0) => coreSync().uploadStallAllowanceMs(Number(stepBytes) || 0);
/**
 * The firebase/storage web SDK's resumable chunks (`UploadTask` in
 * @firebase/storage 0.13, read from its source): the first is 256 KiB, each
 * that lands doubles the next while the doubled size stays under 32 MiB — so
 * the largest is 16 MiB — and a chunk that fails resets it to 256 KiB. A file
 * of 256 KiB or less goes up in one request.
 */
export const FIRST_CHUNK_BYTES = 256 * 1024;
export const MAX_CHUNK_BYTES = 16 * 1024 * 1024;

/**
 * The most the SDK may send before it next reports, with `transferred` of
 * `total` reported: the next chunk, never more than is left. The bytes so
 * far bound it from above — after chunks of 256 KiB, 512 KiB, … the next is
 * the sum plus 256 KiB, and after a reset it is smaller still. A `total` not
 * known (0 or less) is no cap.
 */
export function uploadStep(transferred, total = 0) {
  const sent = Math.max(Number(transferred) || 0, 0);
  const next = Math.min(sent + FIRST_CHUNK_BYTES, MAX_CHUNK_BYTES);
  const whole = Number(total) || 0;
  return whole > 0 ? Math.min(next, Math.max(whole - sent, 0)) : next;
}

/** The sentence a stalled upload fails with — the one every timeout says. */
export const stalledSentence = () => coreSync().uploadMessage('stalled');

/**
 * An upload the teacher cancelled: not a failure, so nothing is said. Its
 * own class so a caller can tell it from a `FileRefused` with a sentence.
 */
export class UploadCancelled extends Error {
  constructor() {
    super(coreSync().uploadMessage('cancelled'));
    this.cancelled = true;
  }
}

export const isUploadCancelled = (err) => Boolean(err?.cancelled);

// `performance.now()` goes forward whatever the wall clock does, and keeps
// counting while a tab is hidden — whose timers the browser slows, which is
// the watcher's own sleep the core's `asleep_ms` forgives.
const monotonic = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/**
 * Run a Storage `UploadTask` (`uploadBytesResumable`) to its end under the
 * watchdog. Resolves with the task's snapshot. Rejects with a `FileRefused`
 * carrying the timeout sentence when the watchdog cancelled it, an
 * `UploadCancelled` when `signal` (an AbortSignal) did, and a `FileRefused`
 * with `uploadErrorMessage`'s sentence for anything else.
 *
 * `onProgress({ transferred, total })` as the bytes go up, starting at 0.
 * `clock`, `every` and `stop` are the timer's, for a test to drive.
 */
export function watchUpload(task, {
  total = 0, onProgress = null, signal = null,
  clock = monotonic, every = (fn, ms) => setInterval(fn, ms), stop = (id) => clearInterval(id),
} = {}) {
  return new Promise((resolve, reject) => {
    const { tickMs } = uploadWatchPolicy();
    let watch = uploadWatchStart(clock());
    let size = Number(total) || 0;
    let why = null; // 'stalled' | 'cancelled'

    const cancel = (reason) => {
      if (why) return;
      why = reason;
      if (typeof task.cancel === 'function') task.cancel();
    };

    if (onProgress) onProgress({ transferred: 0, total });
    // Only the counts are read here; the failure and the finish are the
    // task's own promise's, below.
    const unsubscribe = task.on('state_changed', (snap) => {
      watch = uploadWatchProgress(watch, snap.bytesTransferred, clock());
      if (snap.totalBytes > 0) size = snap.totalBytes;
      if (onProgress) onProgress({ transferred: snap.bytesTransferred, total: snap.totalBytes || total });
    });
    const timer = every(() => {
      watch = uploadWatchLook(watch, clock(), uploadStep(watch.transferred, size));
      if (watch.stalled) cancel('stalled');
    }, tickMs);
    const onAbort = () => cancel('cancelled');
    if (signal) {
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort);
    }

    const finish = () => {
      stop(timer);
      if (typeof unsubscribe === 'function') unsubscribe();
      if (signal) signal.removeEventListener('abort', onAbort);
    };
    task.then(
      (snap) => { finish(); resolve(snap); },
      (err) => {
        finish();
        if (why === 'stalled') reject(new FileRefused(stalledSentence()));
        else if (why === 'cancelled') reject(new UploadCancelled());
        else reject(new FileRefused(uploadErrorMessage(err)));
      },
    );
  });
}
